import { useDispatch, useSelector } from 'react-redux';

import { RootState, AppDispatch } from '../../store/store';
import CorrelativesAdminService from '../../services/settings/CorrelativesAdminService';

import {
  setCorrelativeList,
  setSelectedCorrelative,
  openCorrelativeModal,
  closeCorrelativeModal,
  setIsLoading,
  setIsSubmitting,
  clearErrors,
  updateCorrelativeInList,
} from '../../store/slices/settings/correlativesAdminSlice';

import useAlerts from '../useAlerts';
import useConfirmModal from '../useConfirmModal';
import useLoading from '../useLoading';
import useApiErrorHandler from '../useApiErrorHandler';

export const useCorrelativesAdmin = () => {
  const dispatch: AppDispatch = useDispatch();

  const { show: showAlert } = useAlerts();
  const confirm = useConfirmModal();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { handle } = useApiErrorHandler();

  const {
    list,
    selected,
    showModal,
    isLoading,
    isSubmitting,
  } = useSelector((state: RootState) => state.correlativesAdmin);

  /* =========================
   * 📋 LISTA (TABLA)
   * ========================= */

  const handlerLoadSequences = async () => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();

      const response = await CorrelativesAdminService.listSequences();

      if (response?.statusCode === 200) {
        dispatch(setCorrelativeList(response.data));
      } else {
        showAlert('error', 'Error', 'No se pudieron obtener las secuencias.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };

  /* =========================
   * 🧾 DETALLE + MODAL
   * ========================= */

  const handlerOpenDetailModal = async (sequenceId: number) => {
    try {
      dispatch(setIsLoading(true));
      showLoadingIndicator();

      const response = await CorrelativesAdminService.getById(sequenceId);

      if (response?.statusCode === 200) {
        dispatch(setSelectedCorrelative(response.data));
        dispatch(openCorrelativeModal());
      } else {
        showAlert('error', 'Error', 'No se pudo obtener la secuencia.');
      }
    } catch (error: any) {
      handle(error);
    } finally {
      dispatch(setIsLoading(false));
      hideLoadingIndicator();
    }
  };

  const handlerCloseModal = () => {
    dispatch(closeCorrelativeModal());
    dispatch(clearErrors());
  };

  /* =========================
   * 🔄 RESET INDIVIDUAL
   * ========================= */

  const handlerResetSequence = async (sequenceId: number) => {
    confirm({
      title: '¿Restablecer correlativo?',
      message:
        'Esta acción eliminará correlativos pendientes y reiniciará la numeración desde 1. ¿Deseas continuar?',
      icon: 'warning',
      confirmText: 'Restablecer',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          dispatch(setIsSubmitting(true));
          showLoadingIndicator();

          const response = await CorrelativesAdminService.resetSequence(sequenceId);

          if (response?.statusCode === 200) {
            showAlert(response.type, response.title, response.message);
            handlerCloseModal();
            dispatch(updateCorrelativeInList(response.data));
            handlerLoadSequences();
          } else {
            showAlert('error', 'Error', 'No se pudo restablecer el correlativo.');
          }
        } catch (error: any) {
          handle(error);
        } finally {
          dispatch(setIsSubmitting(false));
          hideLoadingIndicator();
        }
      },
    });
  };

  /* =========================
   * 🔄 RESET GLOBAL
   * ========================= */

  const handlerResetAllSequences = async () => {
    confirm({
      title: '¿Restablecer TODOS los correlativos?',
      message:
        'Esta acción reiniciará todos los correlativos del sistema. No se puede deshacer.',
      icon: 'warning',
      confirmText: 'Restablecer todos',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          dispatch(setIsSubmitting(true));
          showLoadingIndicator();

          const response = await CorrelativesAdminService.resetAllSequences();

          if (response?.statusCode === 200) {
            showAlert(response.type, response.title, response.message);
            handlerLoadSequences();
          } else {
            showAlert('error', 'Error', 'No se pudieron restablecer los correlativos.');
          }
        } catch (error: any) {
          handle(error);
        } finally {
          dispatch(setIsSubmitting(false));
          hideLoadingIndicator();
        }
      },
    });
  };

  return {
    /* estado */
    list,
    selected,
    showModal,
    isLoading,
    isSubmitting,

    /* handlers */
    handlerLoadSequences,
    handlerOpenDetailModal,
    handlerCloseModal,
    handlerResetSequence,
    handlerResetAllSequences,
  };
};
